<script lang="ts">
  import {createEventDispatcher} from 'svelte';
  import {Field, FilterSize, VideoInformation, Icon} from '@components';
  import type {PreviewImage} from '@/types/common';
  import tippy from 'tippy.js';
  import Favorite from '../favorite/Favorite.svelte';
  import {templates} from '@/store';
  import type {Shapes, Tags} from '@/types/filters';
  import FeatureFlag from '@/platform-redesign-components/FeatureFlag.svelte';
  import {current_component} from 'svelte/internal';
  const dispatch = createEventDispatcher();

  let initState: PreviewImage = {
    id: 0,
    src: '',
    title: '',
    content: '',
    hasHover: false
  };

  export let selected = initState;
  export let shape: Shapes = '';
  export let favorites = false;
  export let currentTags: Tags[] = [];

  let selectedTag: Tags = '';

  $: onChangeTag(selectedTag);
  const onChangeTag = (tag: Tags) => {
    if (!!tag) {
      if (!currentTags.find(t => t === tag)) {
        currentTags = [...currentTags, tag];
      }
    }
  };

  const removeTag = (tag: string) => {
    if (currentTags.length == 1) {
      currentTags = [];
      selectedTag = '';
    } else {
      currentTags = currentTags.filter(t => t !== tag);
      selectedTag = '';
    }
    return null;
  };

  const changeVideo = () => {
    dispatch('change-video');
  };

  const toggleFavorites = () => {
    favorites = !favorites;
  };

  let informationEl: HTMLElement;
  $: {
    if (informationEl) {
      tippy(informationEl, {
        theme: 'light',
        content: `<div class="flex flex-col p-2">
            <span class="block mb-2 font-light"><b class="font-bold">Square</b> - Facebook and Instagram posts</span>
            <span class="block mb-2 font-light"><b class="font-bold">Horizontal</b> - Facebook post, Youtube</span>
            <span class="block font-light"><b class="font-bold">Vertical</b> - Instagram Reels, TikTok, Facebook Stories</span>
            </div>`,
        allowHTML: true,
        placement: 'right'
      });
    }
  }
</script>

<FeatureFlag>
  <div slot="new_component" class="choose-templates no-scrollbar overflow-y-scroll border border-gray-300 bg-[#F9FAFB]">
    <div class="flex w-full scroll-pl-6 flex-col {selected.id !== 0 ? 'scroll-show' : ''}">
      <!-- <span
      class="flex items-center justify-end text-right text-xs cursor-pointer underline text-blue-700 font-semibold"
      bind:this={informationEl}
      ><Icon className="w-3 h-3 mr-1 inline-block" name="Info" /> What size do i
      need?</span
    >
    -->
      <h1 class="mb-8 text-2xl font-bold">Filter Templates</h1>

      <div class="flex h-full min-h-max flex-col gap-6">
        <div class="flex flex-col gap-4" id="filter_shape">
          <div class="text-sm font-semibold">
            Size
            <span bind:this={informationEl} class="float-right">
              <i class="fa-regular fa-circle-question"></i>
            </span>
          </div>
          <div class="flex flex-wrap gap-3">
            <FilterSize bind:shape />
          </div>
        </div>

        <div class="flex flex-col gap-4" id="filter_tags">
          <div class="text-sm font-medium">Tags</div>
          <div class="flex flex-col gap-3">
            {#if $templates.tags.length > 0}
              <Field
                type="dropdown"
                placeholder="Select Tags"
                items={$templates.tags}
                isClearable={false}
                bind:value={selectedTag}
              />
              <div>
                {#if currentTags.length > 0}
                  <div class="flex flex-wrap">
                    {#each currentTags as item}
                      <div
                        class="mb-2 mr-2 flex items-center rounded bg-gray-700 p-2 text-sm font-medium leading-none text-white"
                      >
                        {item}
                        <button on:click={removeTag(item)}>
                          <Icon name="Cross" className="w-3 h-3 ml-2 text-red-500 text-opacity-80" />
                        </button>
                        <button />
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </div>

        <div class="flex flex-row items-center" id="filter_favorites">
          <div class="flex-none text-sm font-semibold">
            <i class="fa-solid fa-star text-[#FABE24]" />
            &nbsp; Favorites
          </div>
          <div class="flex-auto" />
          <div class="flex-none">
            <Favorite bind:favorites on:filter-favorite={toggleFavorites} />
          </div>
        </div>
      </div>
      {#if selected.id !== 0}
        <hr class="mb-4 mt-4" />
        <div class="flex flex-col gap-4">
          <div class="font-medium">Your Template</div>
          <div class="preview-bg">
            <img class="w-full" src={selected.src} alt={selected.title} loading="lazy" />
          </div>
        </div>
      {/if}
    </div>
  </div>

  <div slot="current_component" class="choose-templates no-scrollbar overflow-y-scroll">
    <div class="flex w-full scroll-pl-6 flex-col {selected.id !== 0 ? 'scroll-show' : ''}">
      <VideoInformation on:choose-different-video={changeVideo} />
      <hr class="mb-4 mt-4" />
      <span
        class="flex cursor-pointer items-center justify-end text-right text-xs font-semibold text-blue-700 underline"
        bind:this={informationEl}><Icon className="w-3 h-3 mr-1 inline-block" name="Info" /> What size do i need?</span
      >

      <div class="flex h-full min-h-max flex-col gap-6">
        <div class="flex flex-col gap-4" id="filter_shape">
          <div class="font-medium">Size</div>
          <div class="flex flex-wrap gap-3">
            <FilterSize bind:shape />
          </div>
        </div>

        <div class="flex flex-col gap-4" id="filter_favorites">
          <div class="font-medium">Favorites</div>
          <div class="flex flex-wrap gap-3">
            <Favorite bind:favorites on:filter-favorite={toggleFavorites} />
          </div>
        </div>

        <div class="flex flex-col gap-4" id="filter_tags">
          <div class="font-medium">Tags</div>
          <div class="flex flex-col gap-3">
            {#if $templates.tags.length > 0}
              <Field
                type="dropdown"
                placeholder="Choose a keyword"
                items={$templates.tags}
                isClearable={false}
                bind:value={selectedTag}
              />
              <div>
                {#if currentTags.length > 0}
                  <div class="flex flex-wrap">
                    {#each currentTags as item}
                      <div
                        class="mb-2 mr-2 flex items-center rounded bg-gray-700 p-2 text-sm font-medium leading-none text-white"
                      >
                        {item}
                        <button on:click={removeTag(item)}>
                          <Icon name="Cross" className="w-3 h-3 ml-2 text-red-500 text-opacity-80" />
                        </button>
                        <button />
                      </div>
                    {/each}
                  </div>
                {/if}
              </div>
            {/if}
          </div>
        </div>
      </div>
      {#if selected.id !== 0}
        <hr class="mb-4 mt-4" />
        <div class="flex flex-col gap-4">
          <div class="font-medium">Your Template</div>
          <div class="preview-bg">
            <img class="w-full" src={selected.src} alt={selected.title} loading="lazy" />
          </div>
        </div>
      {/if}
    </div>
  </div>
</FeatureFlag>

<style lang="postcss">
  .choose-templates {
    @apply relative z-40 flex h-full w-4/12 flex-col gap-4 p-8;
  }

  .scroll-show {
    @apply pb-5 pr-5;
  }

  .preview-bg img {
    max-width: 115px;
  }

  .no-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .no-scrollbar::-webkit-scrollbar {
    display: none;
  }
</style>
